import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { useToast } from 'src/hooks/use-toast';

const GoogleCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleGoogleRedirectToken } = useAuthActions();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const emailParam = urlParams.get('email');
        const googleError = urlParams.get('error');
        const tokenParam = urlParams.get('access_token');
        const refreshTokenParam = urlParams.get('refresh_token');

        // If we initiated a secondary-link flow, route back to linking page
        const pendingLink = localStorage.getItem('pendingSecondaryLink');
        if (pendingLink === 'google') {
          const returnPath =
            localStorage.getItem('pendingSecondaryLinkReturn') ||
            '/create-link';
          try {
            localStorage.removeItem('pendingSecondaryLink');
            localStorage.removeItem('pendingSecondaryLinkReturn');
          } catch (_) {
            // ignore
          }

          const status = googleError ? 'error' : 'success';
          const search = new URLSearchParams({
            linked: 'google',
            status,
          });
          if (emailParam) search.set('email', emailParam);

          navigate(`${returnPath}?${search.toString()}`, { replace: true });
          setIsProcessing(false);
          return;
        }

        // If Keycloak returned tokens directly in query params, use them
        if (tokenParam) {
          await handleGoogleRedirectToken(
            tokenParam,
            refreshTokenParam || null
          );
          setIsProcessing(false);
          return;
        }
        if (googleError) {
          toast({
            message: `${emailParam} isn't linked. Try another account.`,
            variant: 'destructive',
          });
          return;
        }

        // Success handling is done in handleGoogleCallback
        setIsProcessing(false);
      } catch (error) {
        console.error('Google callback error:', error);
        toast({
          message: error.message || 'Failed to complete Google login',
          variant: 'destructive',
        });
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [location, navigate, toast]);

  if (isProcessing) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Completing login...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;
